import { useState } from "react";
import FileManagerApiClient from "../../../clients/FileManagerApiClient";
import { toast } from "react-toastify";
import {
    CreateDocumentRequest,
    Document,
    DocumentListResponse
} from "../../../model/FileManager";

const fileManagerApi = new FileManagerApiClient();

interface FileManagerState {
    files: Document[];
    isLoading: boolean;
    error: string | null;
}

export const useFileManager = () => {
    const [state, setState] = useState<FileManagerState>({
        files: [],
        isLoading: false,
        error: null,
    });

    const handleError = (message: string) => {
        toast.error(message, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const handleSuccess = (message: string) => {
        toast.success(message, {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const fetchAllFiles = async (
        tenantId: string,
        limit: number = 50,
        offset: number = 0,
        search: string = ''
    ): Promise<DocumentListResponse | null> => {
        try {
            if (!search) {
                setState(prev => ({ ...prev, isLoading: true, error: null }));
            }
            const response = await fileManagerApi.getAllFiles(tenantId, limit, offset, search);
            setState(prev => ({
                ...prev,
                files: response.files,
                isLoading: false
            }));
            return response;
        } catch (error: any) {
            handleError(error.message || "Failed to fetch files");
            setState(prev => ({
                ...prev,
                error: "Failed to fetch files",
                isLoading: false
            }));
            return null;
        }
    };

    const createFile = async (
        file: File,
        fileRequest: CreateDocumentRequest
    ) => {
        try {
            const newFile = await fileManagerApi.uploadFile(fileRequest, file);
            
            setState(prev => ({
                ...prev,
                files: [...prev.files, newFile],
                error: null,
            }));
            
            handleSuccess(`File ${fileRequest.fileName} uploaded successfully!`);
        } catch (error: any) {
            if (error) {
                switch (error.status) {
                    case 409:
                        handleError(error.message);
                        break;
                    default:
                        handleError(error.message);
                }
            } else {
                handleError("Network error occurred");
            }
            setState(prev => ({
                ...prev,
                error: error.message
            }));
        }
    };


    const getUploadingFiles = async (tenantId: string) => {
        try {
            const uploadingFiles = await fileManagerApi.getUploadStatus(tenantId);
            return uploadingFiles || {};
        } catch (error: any) {
            if (error) {
                handleError("unexpected Error")
            } else {
                handleError("Network error occurred");
            }
            setState(prev => ({
                ...prev,
                error: error.message
            }));
            return {};
        }
    };

    const deleteFile = async (fileId: string, tenantId: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true }));
            await fileManagerApi.deleteFile(fileId, tenantId);
            
            setState(prev => ({
                ...prev,
                files: prev.files.filter(file => file.filename !== fileId),
                isLoading: false
            }));
            
            toast.success("File deleted successfully!");
        } catch (error: any) {
            if (error) {
                switch (error.status) {
                    case 403:
                    case 409:
                        handleError(error.message);
                        break;
                    default:
                        handleError("An unexpected error occurred");
                }
            } else {
                handleError("Network error occurred");
            }
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: null,
            }));
        }
    };

    const editFileName = async (fileId: string, newName: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true }));
            await fileManagerApi.editFileName(fileId, newName);
            
            // Update files list after successful rename
            setState(prev => ({
                ...prev,
                files: prev.files.map(file => 
                    file.filename === fileId ? { ...file, name: newName } : file
                ),
                isLoading: false
            }));
            
            toast.success("File renamed successfully!");
        } catch (error: any) {
            handleError(error.message || "Failed to rename file");
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: "Failed to rename file"
            }));
        }
    };

    const openFile = async (tenantId: string, fileName: string) => {
        try {
            setState(prev => ({ ...prev, isLoading: true }));
            const response = await fileManagerApi.getFilePresignedUrl(fileName, tenantId);
            setState(prev => ({ ...prev, isLoading: false }));
            return response;
        } catch (error: any) {
            handleError(error.message || "Failed to open file");
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: "Failed to open file"
            }));
        }
    };

    return {
        files: state.files,
        isLoading: state.isLoading,
        error: state.error,
        createFile,
        deleteFile,
        editFileName,
        openFile,
        fetchAllFiles,
        getUploadingFiles
    };
};